import { apiClient } from './client'
import i18n from '@/i18n/config'
import { API_ENDPOINTS } from '@/utils/constants'

export type ChatRoomType = 'direct' | 'group' | 'lesson'
export type LessonChatStatus = 'planned' | 'in_progress' | 'completed' | 'canceled'
export type ChatRoomMemberRole = 'member' | 'admin'
export type ChatAttachmentType = 'image' | 'file' | 'sticker'

/** Whitelist emoji-реакций — зеркало config('chat.reactions') backend. */
export const CHAT_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉'] as const

/**
 * Таймаут загрузки вложения, мс. Считается от размера файла: axios отмеряет
 * время на весь запрос вместе с телом, поэтому фиксированное значение на
 * медленном аплинке обрывало бы большие файлы, которые дошли бы сами.
 * Минута на старт плюс минута на мегабайт — 10 МБ (потолок вложения) при
 * 150 кбит/с ещё успевают.
 */
function chatUploadTimeoutMs(fileSizeBytes: number): number {
  return 60_000 + Math.ceil(fileSizeBytes / (1024 * 1024)) * 60_000
}

export interface ChatAttachment {
  type: ChatAttachmentType
  path: string
  name: string
  url: string | null
  mime: string | null
  size: number | null
  /** Только для type=sticker — id стикера каталога на момент отправки. */
  sticker_id?: number | null
}

/**
 * Агрегат реакций сообщения. Поля reacted_by_me в контракте нет —
 * свою реакцию клиент определяет по user_ids (broadcast идёт без request-user).
 */
export interface ChatReactionAggregate {
  emoji: string
  count: number
  user_ids: number[]
}

export interface ChatSticker {
  id: number
  code: string
  emoji: string | null
  url: string
  width: number | null
  height: number | null
  is_active: boolean
  sort_order: number
}

export interface ChatStickerPack {
  id: number
  code: string
  title: string
  is_active: boolean
  /** Набор виден и доступен для отправки только админам. */
  admin_only: boolean
  sort_order: number
  stickers?: ChatSticker[]
  stickers_count?: number
  /** Привязанные тарифы (пусто — базовый набор); только в admin-ответах. */
  subscription_plan_ids?: number[]
  subscription_plans?: Array<{ id: number; name: string }>
}

export interface ChatMessageAuthor {
  id: number
  full_name: string
  avatar_url: string | null
}

/**
 * Служебная разметка сообщения. Незнакомый kind клиент игнорирует — сервер
 * вправе добавить новые виды разметки, не ломая ленту.
 */
export interface LessonChatWidgetMeta {
  kind: 'lesson_chat_widget'
  lesson_id: number
  /** Комната урока для read-only просмотра истории. */
  lesson_chat_room_id: number
  lesson_date: string | null
  lesson_status: 'completed' | 'canceled'
}

export type ChatMessageMeta = LessonChatWidgetMeta | { kind: string }

export function isLessonChatWidget(
  meta: ChatMessageMeta | null | undefined
): meta is LessonChatWidgetMeta {
  return meta?.kind === 'lesson_chat_widget'
}

export interface ChatMessage {
  id: number
  chat_room_id: number
  author_user_id: number
  text: string | null
  attachments: ChatAttachment[]
  /** Служебная разметка ленты; у обычного сообщения null. */
  meta?: ChatMessageMeta | null
  /** Отсутствует в last_message списка комнат (там реакции не грузятся). */
  reactions?: ChatReactionAggregate[]
  /** Монотонная версия агрегата реакций — кадры со старой версией отбрасываются. */
  reactions_version?: number
  is_deleted: boolean
  edited_at: string | null
  created_at: string | null
  updated_at: string | null
  author?: ChatMessageAuthor | null
}

export interface ChatRoomMember {
  user_id: number
  role: ChatRoomMemberRole
  last_read_message_id: number | null
  muted_until: string | null
  user: ChatMessageAuthor | null
}

export interface LessonChatRoomLesson {
  id: number
  status: LessonChatStatus
  planned_start_at: string | null
  actual_start_at: string | null
}

export interface ChatRoom {
  id: number
  type: ChatRoomType
  student_group_id: number | null
  /** Только у комнаты урока (type=lesson). */
  lesson?: LessonChatRoomLesson | null
  unread_count: number
  can_write: boolean
  is_muted: boolean
  title: string | null
  group: { id: number; name: string } | null
  members?: ChatRoomMember[]
  last_message?: ChatMessage | null
  created_at: string | null
  updated_at: string | null
}

export interface ChatContact {
  user: ChatMessageAuthor
  existing_room_id: number | null
}

export interface MessagesPage {
  data: ChatMessage[]
  meta: {
    has_more: boolean
    next_before_id: number | null
  }
}

export type SendMessageAttachmentPayload =
  | {
      type: 'image' | 'file'
      path: string
      name: string
      mime: string
      size: number
    }
  | {
      /** Стикер: сервер сам собирает snapshot path/name/mime/size из каталога. */
      type: 'sticker'
      sticker_id: number
    }

export interface SendMessagePayload {
  text?: string | null
  attachments?: SendMessageAttachmentPayload[]
}

export interface ReactionsResult {
  message_id: number
  chat_room_id: number
  reactions: ChatReactionAggregate[]
  reactions_version: number
}

interface ApiEnvelope<T> {
  message?: string
  data?: T
}

function unwrapData<T>(response: { data: ApiEnvelope<T> }, fallbackMessage: string): T {
  if (response.data?.data !== undefined) {
    return response.data.data
  }
  throw new Error(response.data?.message || fallbackMessage)
}

export const chatApi = {
  getRooms: async (): Promise<ChatRoom[]> => {
    const response = await apiClient.get<ApiEnvelope<ChatRoom[]>>(API_ENDPOINTS.CHAT.ROOMS)
    return response.data.data ?? []
  },

  /**
   * Комната чата урока. У идущего урока сервер создаёт её при первом
   * обращении, у остальных отдаёт только существующую — иначе null. В getRooms
   * такие комнаты не приходят.
   */
  getLessonRoom: async (lessonId: number): Promise<ChatRoom | null> => {
    const response = await apiClient.get<ApiEnvelope<ChatRoom | null>>(
      API_ENDPOINTS.CHAT.LESSON_ROOM(lessonId)
    )
    return response.data.data ?? null
  },

  getContacts: async (): Promise<ChatContact[]> => {
    const response = await apiClient.get<ApiEnvelope<ChatContact[]>>(API_ENDPOINTS.CHAT.CONTACTS)
    return response.data.data ?? []
  },

  openDirect: async (userId: number): Promise<ChatRoom> => {
    const response = await apiClient.post<ApiEnvelope<ChatRoom>>(
      API_ENDPOINTS.CHAT.OPEN_DIRECT(userId)
    )
    return unwrapData(response, '')
  },

  getMessages: async (
    roomId: number,
    params?: { before_id?: number; limit?: number }
  ): Promise<MessagesPage> => {
    const search = new URLSearchParams()
    if (params?.before_id) search.append('before_id', String(params.before_id))
    if (params?.limit) search.append('limit', String(params.limit))
    const query = search.toString()
    const url = query
      ? `${API_ENDPOINTS.CHAT.MESSAGES(roomId)}?${query}`
      : API_ENDPOINTS.CHAT.MESSAGES(roomId)
    const response = await apiClient.get<MessagesPage>(url)
    return response.data
  },

  sendMessage: async (roomId: number, payload: SendMessagePayload): Promise<ChatMessage> => {
    const response = await apiClient.post<ApiEnvelope<ChatMessage>>(
      API_ENDPOINTS.CHAT.MESSAGES(roomId),
      payload
    )
    return unwrapData(response, '')
  },

  editMessage: async (messageId: number, payload: SendMessagePayload): Promise<ChatMessage> => {
    const response = await apiClient.patch<ApiEnvelope<ChatMessage>>(
      API_ENDPOINTS.CHAT.MESSAGE(messageId),
      payload
    )
    return unwrapData(response, '')
  },

  deleteMessage: async (messageId: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.CHAT.MESSAGE(messageId))
  },

  markRead: async (roomId: number, upTo: number): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CHAT.READ(roomId), { up_to: upTo })
  },

  enterPresence: async (roomId: number): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CHAT.PRESENCE(roomId))
  },

  leavePresence: async (roomId: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.CHAT.PRESENCE(roomId))
  },

  /**
   * У apiClient таймаута нет (axios по умолчанию ждёт бесконечно), а вложения
   * грузятся по одной пачке за раз — подвисший запрос запер бы вложения и
   * отправку до перезагрузки страницы.
   */
  uploadFile: async (roomId: number, file: File): Promise<ChatAttachment> => {
    const form = new FormData()
    form.append('file', file)
    const response = await apiClient.post<ApiEnvelope<ChatAttachment>>(
      API_ENDPOINTS.CHAT.UPLOAD(roomId),
      form,
      { timeout: chatUploadTimeoutMs(file.size) }
    )
    return unwrapData(response, '')
  },

  mute: async (roomId: number, until: string | null): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CHAT.MUTE(roomId), { until })
  },

  toggleReaction: async (messageId: number, emoji: string): Promise<ReactionsResult> => {
    const response = await apiClient.post<ApiEnvelope<ReactionsResult>>(
      API_ENDPOINTS.CHAT.REACTIONS(messageId),
      { emoji }
    )
    return unwrapData(response, i18n.t('common.error'))
  },

  /** room_id — контекст комнаты для тарифного фильтра (план учителя комнаты). */
  getStickerPacks: async (roomId?: number): Promise<ChatStickerPack[]> => {
    const response = await apiClient.get<ApiEnvelope<ChatStickerPack[]>>(
      API_ENDPOINTS.CHAT.STICKER_PACKS,
      { params: roomId !== undefined ? { room_id: roomId } : undefined }
    )
    return response.data.data ?? []
  },
}

